<script>
  import TextWithLabel from '#lib/TextWithLabel.svelte';
  import EntrySelectorDrawer from '#lib/EntrySelectorDrawer.svelte';
  import Image from '#lib/Image.svelte';
  import ZoomableImage from '#lib/ZoomableImage.svelte';
  import { fetch_entries } from '#lib/util.js';
  import t from '#lib/i18n.svelte.js';
  import DraggableList from '#lib/DraggableList.svelte';
  import DraggableItem from '#lib/DraggableItem.svelte';
  import BlockGrid from '#lib/BlockGrid.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import { entry_url, has_access, create_message_link } from '#lib/util.js';
  import { page } from '$app/state';
  import { getContext } from 'svelte';
  import Alert from '#lib/Alert.svelte';
  import { fetch_json } from '#lib/util.js';
  import { toast } from '#lib/toast.svelte.js';
  let site_settings = getContext('site_settings');
  let entry_api = getContext("entry_api");

  let { 
    entry, 
    is_editing 
  } = $props();

  // Who may change what: the owner edits title and introduction; with the site setting on, a
  // member owner also uploads pictures and writes their captions (admins always may)
  let is_admin = $derived(page.data.current_user?.role === 'admin');
  let is_owner = $derived(!!page.data.current_user && entry_api?.entry?.user_id === page.data.current_user?.user_id);
  let can_edit_texts = $derived(is_admin || is_owner);
  // Pictures go into a saved collection: an unsaved one has no id to upload to yet
  let can_upload = $derived(!!entry.entry_id && (is_admin || (is_owner && site_settings.allow_member_collection_images)));
  function can_caption(entry_preview) {
    if (entry_preview.type !== 'image') return false;
    return is_admin || (site_settings.allow_member_collection_images && entry_preview.user_id === page.data.current_user?.user_id);
  }

  // Pictures upload one after another; each becomes an image entry the collection then lists
  let upload_input = $state();
  let upload_state = $state({ done: 0, total: 0, errors: [] });
  async function upload_pictures(files) {
    const list = [...(files ?? [])].filter(file => file.type.startsWith('image/'));
    if (list.length === 0) return;
    upload_state = { done: 0, total: list.length, errors: [] };
    for (const file of list) {
      const body = new FormData();
      body.append('file', file);
      body.append('collection_id', String(entry.entry_id));
      try {
        const response = await fetch('/api/upload-collection-image', { method: 'POST', body });
        if (!response.ok) throw new Error(await response.text());
        const { entry_id } = await response.json();
        entry.entry_previews_multiref.push(entry_id);
      } catch (error) {
        console.error('Picture upload failed', error);
        upload_state.errors.push(file.name);
      }
      upload_state.done += 1;
    }
    if (upload_input) upload_input.value = '';
  }

  // Captions save on their own (the picture is its own entry), when the field is left.
  // A refused save says so and puts the stored text back.
  async function save_caption(entry_preview, field, value) {
    const previous = entry_preview[field] ?? '';
    if (previous === value) return;
    entry_preview[field] = value;
    try {
      await fetch_json('POST', '/api/update-image-caption', {
        entry_id: entry_preview.entry_id,
        title: entry_preview.title ?? '',
        description: entry_preview.description ?? '',
      });
    } catch (error) {
      console.error('Saving the caption failed', error);
      entry_preview[field] = previous;
      toast.error(t('save_failed'));
    }
  }

  // UI State
  let selected_entry_previews_layout = $derived(Number(page.url.searchParams.get('layout')) || entry.entry_previews_layout || 1);

  function create_collection_message_link(entry) {
    if(!page.data.current_user) {
      return `/login?return_to=${encodeURIComponent(`${entry.slug}-${entry.entry_id}`)}`;
    }
    const collection_info = `${t('collection_message_enquire_subject')} "${entry.title}".\n${page.url.href}\n\n`;
    return create_message_link(collection_info);
  }
  
  $effect.pre(() => {
    const multiref = entry.entry_previews_multiref;
    // Skip fetch when local entry_previews already match multiref
    const current_ids = entry.entry_previews?.map(ep => ep.entry_id) ?? [];
    if (multiref.length === 0 || JSON.stringify(multiref) === JSON.stringify(current_ids)) return;
    fetch_entries(multiref, {as_preview: true}).then(entry_previews => {
      entry.entry_previews = entry_previews;
    });
  });

  let enquiry_link = $derived.by(() => {
    return create_collection_message_link(entry)
  });

  // Detect if current user lacks member access and some previews are hidden
  const user_is_member_or_admin = $derived(!!page.data.current_user && (page.data.current_user.role === 'member' || page.data.current_user.role === 'admin'));
  const show_member_cta = $derived(!user_is_member_or_admin && entry.hidden_entry_previews_count > 0);

  /**
   * Calculate the appropriate sizes attribute based on the selected layout and its responsive behavior.
   * Uses calc() to account for page padding and grid gaps rather than raw vw fractions.
   */
  let image_sizes = $derived.by(() => {
    switch(selected_entry_previews_layout) {
      case 1: // 3-columns layout: 2 cols mobile, 3 cols desktop (769px+)
        return '(min-width: 769px) calc((100vw - 72px) / 3), calc((100vw - 32px) / 2)';
      case 2: // 4-columns layout: 2 cols mobile, 4 cols desktop (769px+)
        return '(min-width: 769px) calc((100vw - 80px) / 4), calc((100vw - 32px) / 2)';
      case 3: // List layout: 1 column. width = calc(2 * var(--track-inner) + var(--grid-gutter)) = ~100px
        return '100px';
      default:
        return '100vw';
    }
  });

</script>

<BlockGrid>
  <div 
    class="collection-entry previews-layout-{selected_entry_previews_layout} page-layout-{entry.page_layout}"
  >
    <header>
      <div class='text-fields'>
        {#if entry.preamble || (is_editing && has_access('admin', page.data.current_user?.role))}
          <TextWithLabel 
            editable={is_editing && has_access('admin', page.data.current_user?.role)}
            variant='preamble'
            class='collection-preamble'
            bind:content={entry.preamble}
            label={t('preamble')}
            placeholder={t('preamble')}
          />
        {/if}

        {#if entry.title || is_editing}
          <TextWithLabel 
            editable={is_editing}
            variant='heading1'
            class='collection-title'
            bind:content={entry.title}
            label={t('title')}
            placeholder={t('title')}
          />
        {/if}

        {#if entry.description || (is_editing && can_edit_texts)}
          <TextWithLabel 
            editable={is_editing && can_edit_texts}
            variant='body'
            class='collection-description'
            bind:content={entry.description}
            label={t('description')}
            placeholder={t('description')}
          />
        {/if}

        {#if !is_editing && page.data.current_user?.role === 'member'
          && entry_api?.entry?.user_id === page.data.current_user?.user_id
        }
          <button onclick={() => {
            entry_api.start_editing();
        }}>
            <!-- Temporary solution: display the edit button inline, so novice members can discover more easily. -->
            <!-- TODO: Make the edit button more prominent. E.g. a floating action button. Or flash the button once ... -->
            <ButtonLabel 
              start_icon='pencil' 
              variant='primary {site_settings.primary_control_surface}'
              align={site_settings.control_surface_align}
            >
              {t('edit')}
            </ButtonLabel>
          </button>
        {/if}
        {#if is_editing && ((has_access('member', page.data.current_user?.role) && is_owner) || can_upload)}
        <div class='collection-member-tools'>
        {#if is_editing && has_access('member', page.data.current_user?.role)
          && entry_api?.entry?.user_id === page.data.current_user?.user_id
        }  
          <EntrySelectorDrawer
            excluded_ids={entry.entry_previews_multiref}
            label={t('add')}
            icon='plus'
            variant='primary {site_settings.primary_control_surface}'
            multi_select={true}
            confirm_label={t('add_selected_entry')}
            confirm_label_plural={t('add_selected_entries', { count: '{{count}}' })}
            onconfirm={(entry_ids) => {
              for (const entry_id of entry_ids) {
                entry.entry_previews_multiref.push(entry_id);
              }
            }}
          />
        {/if}
        {#if is_editing && can_upload}
          <div class='upload-pictures flex-row items-center gap-x g-4'>
            <label class='upload-trigger'>
              <input
                bind:this={upload_input}
                type='file'
                accept='image/*'
                multiple
                class='visually-hidden'
                onchange={(e) => upload_pictures(e.currentTarget.files)}
                disabled={upload_state.done < upload_state.total}
              />
              <ButtonLabel 
                start_icon='plus'
                variant='primary {site_settings.primary_control_surface}'
                align={site_settings.control_surface_align}
              >
                {t('upload_pictures')}
              </ButtonLabel>
            </label>
            {#if upload_state.done < upload_state.total}
              <span class='text-caption text-2' role='status'>{t('uploading_n_of_m', { n: upload_state.done + 1, m: upload_state.total })}</span>
            {/if}
            {#each upload_state.errors as name}
              <span class='text-caption' role='alert'>{t('upload_failed_named', { name })}</span>
            {/each}
          </div>
        {/if}
        </div>
        {/if}
      </div>

      {#if is_editing || (entry.viewer_available_layouts.length > 1 && entry.entry_previews.length > 0)}
        <div class='display-and-controls gap-x gap-y g-10'>
          {#if entry.allow_enquiry && entry.entry_previews.length > 0}
            <a 
              href={is_editing ? undefined : enquiry_link} 
              class:disabled={is_editing}
              rel='noopener noreferrer nofollow'
              data-sveltekit-preload-data="false"
            >
              <ButtonLabel variant='brand {site_settings.primary_control_surface}'>{t('enquire_about_collection')}</ButtonLabel>
            </a>
          {/if}

          {#if entry.entry_previews.length > 3}
            <div class='display-controls flex-row gap-x g-2'>
              {#if entry.viewer_available_layouts.includes(1) && entry.entry_previews.length > 3}
                <a href={'?layout=1'} onclick={() => ( is_editing && (entry.entry_previews_layout = 1))}>
                  <ButtonLabel variant='secondary {selected_entry_previews_layout === 1 ? 'tab-selected' : 'tab'}' start_icon='three-columns' />
                </a>
              {/if}
              {#if entry.viewer_available_layouts.includes(2) && entry.entry_previews.length > 4}
                <a href={'?layout=2'} onclick={() => ( is_editing && (entry.entry_previews_layout = 2))}>
                  <ButtonLabel variant='secondary {selected_entry_previews_layout === 2 ? 'tab-selected' : 'tab'}' start_icon='four-columns' />
                </a>
              {/if}
              {#if entry.viewer_available_layouts.includes(3)}
                <a href={'?layout=3'} onclick={() => ( is_editing && (entry.entry_previews_layout = 3))}>
                  <ButtonLabel variant='secondary {selected_entry_previews_layout === 3 ? 'tab-selected' : 'tab'}' start_icon='image-list' />
                </a>        
              {/if}
            </div>
          {/if}
        </div>
      {/if}

    </header>

    {#if entry.entry_previews.length > 0}
      <div class='collection-display'>
        <DraggableList
          bind:list={entry.entry_previews_multiref} 
          class='collection-entry-previews'
          direction={selected_entry_previews_layout === 3 ? 'column' : 'row'}
        >
          {#each entry.entry_previews as entry_preview, index}
            <!-- A picture's own page is a dead end: works open in the zoom view, other entries
                 (e.g. a series as a sub-collection) link to their page -->
            {@const is_picture = entry_preview.type === 'image'}
            <DraggableItem 
              {entry_preview} 
              {index} 
              {is_editing}
              align='items-start'
            >
            <svelte:element
              this={is_picture || is_editing ? 'div' : 'a'}
              {...(!is_picture && !is_editing ? {
                href: entry_url(entry_preview)
              } : {})}
                class="entry-preview gap-y gap-x g-4 mbe m-block-m {selected_entry_previews_layout === 3 ? 'flex-row' : 'flex-column'}"
                class:cursor-grab={is_editing}
              >
                {#if is_editing}
                  <ButtonLabel 
                    class='drag-handle drag-image-ignore' 
                    variant='primary ghostly' 
                    start_icon="drag-handle" 
                    align='center'
                  />
                {/if}
                {#if is_editing}
                  <button
                    class='delete-handle drag-image-ignore' 
                    onclick={() => {
                    entry.entry_previews_multiref.splice(index, 1);
                  }}>
                    <ButtonLabel 
                      variant='primary ghostly' 
                      start_icon="close" 
                      align='center'
                    />
                  </button>
                {/if}
                {#if entry_preview.images && entry_preview.images.length > 0}
                  <div class="preview-images">
                    {#if is_picture}
                      <ZoomableImage
                        {...entry_preview.images[0]}
                        zoom={site_settings.image_zoom && !is_editing && !entry_preview.images[0]?.src?.toLowerCase().endsWith('.svg')}
                        title={entry_preview.title}
                        description={entry_preview.description}
                        is_editing={is_editing}
                        sizes={image_sizes}
                      />
                    {:else}
                      <Image
                        {...entry_preview.images[0]}
                        title={entry_preview.title}
                        description={entry_preview.description}
                        is_editing={is_editing}
                        sizes={image_sizes}
                      />
                    {/if}
                  </div>
                {/if}
                <div class="entry-preview-text flex-column gap-y g-4">
                  {#if is_editing && can_caption(entry_preview)}
                    <!-- A work's title and its one line of details, saved when the field is left -->
                    <div class='caption-fields drag-image-ignore'>
                      <input
                        class='caption-input text-control'
                        aria-label={t('picture_title')}
                        placeholder={t('picture_title')}
                        value={entry_preview.title ?? ''}
                        draggable='false'
                        onblur={(e) => save_caption(entry_preview, 'title', e.currentTarget.value.trim())}
                      />
                      <input
                        class='caption-input text-control'
                        aria-label={t('picture_details')}
                        placeholder={t('picture_details')}
                        value={entry_preview.description ?? ''}
                        draggable='false'
                        onblur={(e) => save_caption(entry_preview, 'description', e.currentTarget.value.trim())}
                      />
                    </div>
                  {:else if entry_preview.type === 'image' && selected_entry_previews_layout !== 3}
                    {#if entry_preview.title}
                      <p class="work-title text-body leading-trim">{entry_preview.title}</p>
                    {/if}
                    {#if entry_preview.description}
                      <p class="work-details text-caption text-2 leading-trim">{entry_preview.description}</p>
                    {/if}
                  {:else if entry_preview.title 
                      && (entry_preview.description || entry_preview.preamble)
                      && selected_entry_previews_layout === 3
                    }
                    <h3 class="text-heading3 leading-trim">{entry_preview.title}</h3>
                    {#if selected_entry_previews_layout === 3}
                      <p class="text-body leading-trim">{entry_preview.description}</p>
                    {/if}
                    {#if entry_preview.preamble}
                      <p class="text-preamble leading-trim">{entry_preview.preamble}</p>
                    {/if}
                  {:else if entry_preview.title}
                    <p class="text-preamble leading-trim">
                      {entry_preview.title}
                    </p>
                  {/if}
                </div>
              </svelte:element>
            </DraggableItem>
          {/each}
        </DraggableList>
      </div>
    {:else if entry.user_id === page.data.current_user?.user_id}
      <Alert class='text-2 mbs m-block-s'>{t('no_collection_entries_hint')}</Alert>
    {:else}
      <Alert class='text-2 mbs m-block-s'>{t('empty_collection')}</Alert>
    {/if}

  </div>
</BlockGrid>

{#if show_member_cta}
  <div class="member-cta-wrapper">
    <h2 class="text-heading2">{t('to_view_full_collection_become_member')}</h2>
    <a href="/login" rel="noopener noreferrer" data-sveltekit-preload-data="false">
      <ButtonLabel variant='primary {site_settings.primary_control_surface}' align='center'>
        {t('become_a_member')}
      </ButtonLabel>
    </a>
  </div>
{/if}




<style lang='postcss'>
  .collection-entry {
    &.previews-layout-2 {
      --column-count: 2;
    }
    &.previews-layout-1 {
      --column-count: 2;
    }
    &.previews-layout-3  {
      --column-count: 1;        
    }
    @media (min-width: 769px) {
      &.previews-layout-1 {
        --column-count: 3;
      }
      &.previews-layout-2 {
        --column-count: 4;
      }
    }
    &.previews-layout-3  {
      .preview-images {
        :global(.media-item){
          width: calc(2 * var(--track-inner) + var(--grid-gutter));
        }
      }
    }
    display: flex;
    flex-direction: column;
    --list-item-inline-spacing: 0;
    --list-item-block-spacing: var(--size-3);
    header {
      margin-block-start: var(--block-spacing-m);
      gap: var(--block-spacing-s);
      display: flex;
      .text-fields {
        display: flex;
        flex-direction: column;
        gap: var(--block-spacing-s);
        :global(> *) {
          min-width: min(max(33%, 40ch), 100%);
        }
      }
      .display-and-controls {
        min-width: 180px;
        justify-content: flex-end;
        display: flex;
        align-items: flex-start;
      }
    }
    /* left aligned wide */
    /* center aligned wide */
    &.page-layout-1, &.page-layout-2 {
      grid-column: 2 / 50;
    }
    /* left aligned normal */
    /* center aligned normal */
    &.page-layout-3, &.page-layout-4 {
      grid-column: 10 / 42;
    }
    /* left aligned wide */
    &.page-layout-1, &.page-layout-3 {
      header {
        flex-direction: row;
        justify-content: space-between;
        @media (max-width: 640px) {
          flex-direction: column;
          .display-and-controls {
            justify-content: center;
          }
        }
      }
    }
    /* center aligned wide  */
    &.page-layout-2, &.page-layout-4 {
      header {
        flex-direction: column;
        .text-fields {
          align-items: center;
          justify-items: center;
        }
        .display-and-controls {
          display: flex;
          justify-content: center;
        }
        :global(:is(
          .collection-preamble,
          .collection-title,
          .collection-description
          )) {
          :global(.text) {
            text-align: center;
            --text-align: center;
          }
        }
      }
    }
  }

  /* Works keep their own proportions: never cropped, never letterboxed, the caption right under
     its picture. Browsers with grid lanes (masonry) close the gaps below shorter works; the others
     show a grid whose items keep their height, so a short work leaves space below its caption. */
  :global(.collection-entry-previews) {
    display: grid;
    grid-template-columns: repeat(var(--column-count), minmax(0, 1fr));
    column-gap: var(--grid-gutter);
    align-items: start;
    margin-top: var(--block-spacing-s);
    @supports (display: grid-lanes) {
      display: grid-lanes;
    }
  }

  .preview-images {
    display: flex;
    flex-direction: column;
    :global(> *){
      width: 100%;
      height: auto;
    }
  }

  .entry-preview {
    position: relative;
    :global(.drag-handle), :global(.delete-handle) {
      position: absolute;
      top: var(--size-3);
      left: var(--size-2);
      width: fit-content;
      z-index: 1;
      border: 1px solid transparent;
    }
    :global(.delete-handle) {
      left: auto;
      right: var(--size-2);
      cursor: pointer;
    }
    &:hover :global(.drag-handle), &:hover :global(.delete-handle) {
      border-color: var(--color-default-stroke);
      background-color: var(--color-default-background) !important;
    }
    &:hover .entry-preview-text {
      opacity: 0.7;
      transition: opacity 150ms;
    }
    .entry-preview-text {
      width: 100%;
    }
    .entry-preview-text h3 + *{
      margin-top: var(--block-spacing-xs);
    }
  }

  .collection-member-tools {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--size-4);
    :global(> *) {
      flex: none;
    }
  }
  .upload-trigger {
    cursor: pointer;
    input {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
    }
    &:focus-within :global(.button-label) {
      outline: 2px solid var(--color-foreground);
      outline-offset: 2px;
    }
  }
  .caption-fields {
    display: flex;
    flex-direction: column;
    gap: var(--size-2);
    width: 100%;
  }
  .caption-input {
    width: 100%;
    min-height: var(--input-min-height);
    padding-inline: var(--size-4);
    border: 1px solid var(--color-default-stroke);
    border-radius: var(--input-border-radius);
    background: var(--color-background);
    color: inherit;
    &::placeholder {
      color: var(--text-3);
    }
  }
  .work-title {
    margin: 0;
  }
  .work-details {
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .member-cta-wrapper {
    margin-block: var(--block-spacing-l);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--block-spacing-xs);
    text-align: center;
  }
</style>